import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

export function resolveAtharVariant(variants, variantId, selectedValues) {
  if (!Array.isArray(variants) || variants.length === 0) return null;

  const currentVariant = variants.find((variant) => String(variant.id) === String(variantId)) || null;
  if (selectedValues.length === 0) {
    return currentVariant || variants.find((variant) => variant.available) || variants[0];
  }
  if (selectedValues.some((value) => value === null)) return currentVariant;

  return (
    variants.find(
      (variant) =>
        variant.options.length === selectedValues.length &&
        variant.options.every((option, index) => option === selectedValues[index])
    ) || null
  );
}

export function resolveAtharPostAddAction({ isBuyNow, canOpenCartDrawer }) {
  if (isBuyNow) return 'redirect';
  return canOpenCartDrawer ? 'drawer' : 'notification';
}

/**
 * Connects the Flow 3 product controls to Shopify's selected variant and Ajax
 * cart state. One resolved variant drives price, SKU, material, availability,
 * media, quantity rules, and the submitted merchandise ID.
 */
function initializeAtharProduct(root = document) {
  root.querySelectorAll('[data-athar-product]').forEach((productRoot) => {
    if (productRoot.dataset.atharProductReady === 'true') return;
    productRoot.dataset.atharProductReady = 'true';

    const form = productRoot.querySelector('[data-athar-product-form]');
    const variantInput = productRoot.querySelector('[data-athar-variant-id]');
    const variantData = productRoot.querySelector('[data-athar-variants]');
    if (!(form instanceof HTMLFormElement) || !(variantInput instanceof HTMLInputElement) || !variantData) return;

    let variants = [];
    try {
      variants = JSON.parse(variantData.textContent || '[]');
    } catch (error) {
      console.error('[ATHAR product] Variant data could not be parsed.', error);
    }

    const price = productRoot.querySelector('[data-athar-price]');
    const comparePrice = productRoot.querySelector('[data-athar-compare-price]');
    const availability = productRoot.querySelector('[data-athar-availability]');
    const material = productRoot.querySelector('[data-athar-material]');
    const sku = productRoot.querySelector('[data-athar-sku]');
    const skuValue = sku?.querySelector('span');
    const message = productRoot.querySelector('[data-athar-form-message]');
    const buyNow = productRoot.querySelector('[data-athar-buy-now]');
    const buyNowLabel = productRoot.querySelector('[data-athar-buy-now-label]');
    const addToCart = productRoot.querySelector('[data-athar-add-to-cart]');
    const addToCartLabel = productRoot.querySelector('[data-athar-add-to-cart-label]');
    const checkoutUrl = buyNow?.dataset.atharCheckoutUrl || `${window.Shopify?.routes?.root || '/'}checkout`;
    const quantity = productRoot.querySelector('[data-athar-quantity-input]');
    const optionFieldsets = Array.from(productRoot.querySelectorAll('[data-athar-option-index]'));
    const materialOptionIndex = Number(productRoot.dataset.materialOptionIndex);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let selectedVariant = variants.find((variant) => String(variant.id) === variantInput.value) || null;

    /** Show a concise recoverable form status without destroying entered properties. */
    const setMessage = (text = '') => {
      if (!message) return;
      message.textContent = text;
      message.hidden = !text;
    };

    /** Keep the main media and only the product thumbnail strip in sync. */
    const showMedia = (mediaId, activeButton = null) => {
      if (!mediaId) return;

      productRoot.querySelectorAll('[data-athar-media]').forEach((item) => {
        item.classList.toggle('is-hidden', item.dataset.atharMedia !== String(mediaId));
      });

      const thumbnails = Array.from(productRoot.querySelectorAll('[data-athar-thumbnail]'));
      const selectedButton = activeButton || thumbnails.find((button) => (button.dataset.atharMediaTarget || button.dataset.atharThumbnail) === String(mediaId));
      thumbnails.forEach((button) => {
        const active = button === selectedButton;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const thumbnailList = selectedButton?.closest('[data-athar-thumbnail-list]');
      if (selectedButton instanceof HTMLElement && thumbnailList instanceof HTMLElement) {
        const scrollLeft = selectedButton.offsetLeft - (thumbnailList.clientWidth - selectedButton.offsetWidth) / 2;
        thumbnailList.scrollTo({ left: Math.max(0, scrollLeft), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    };

    const selectedOptions = () => optionFieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);

    /** Disable only combinations that do not exist; sold-out variants remain selectable and report their state. */
    const updateOptionAvailability = (values) => {
      optionFieldsets.forEach((fieldset, optionIndex) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = [...values];
          candidate[optionIndex] = input.value;
          const combinationExists = variants.some((variant) =>
            variant.options.every((option, index) => candidate[index] === null || option === candidate[index])
          );
          input.disabled = !combinationExists && !input.checked;
        });
      });
    };

    /** Apply variant-specific quantity constraints and clamp the current value. */
    const updateQuantityRule = (variant) => {
      if (!(quantity instanceof HTMLInputElement)) return;
      const rule = variant?.quantityRule || { min: 1, max: null, increment: 1 };
      quantity.min = String(Number(rule.min) || 1);
      quantity.step = String(Number(rule.increment) || 1);
      if (rule.max === null || rule.max === undefined || rule.max === '') quantity.removeAttribute('max');
      else quantity.max = String(rule.max);
      normalizeQuantity();
    };

    /** Enforce min/max/increment for buttons and manual quantity entry. */
    const normalizeQuantity = () => {
      if (!(quantity instanceof HTMLInputElement)) return 1;
      const min = Number(quantity.min) || 1;
      const step = Number(quantity.step) || 1;
      const max = quantity.max ? Number(quantity.max) : Number.POSITIVE_INFINITY;
      const raw = Number(quantity.value);
      const finiteValue = Number.isFinite(raw) ? raw : min;
      const steppedValue = min + Math.round((finiteValue - min) / step) * step;
      const normalized = Math.min(max, Math.max(min, steppedValue));
      quantity.value = String(normalized);
      return normalized;
    };

    /** Render every variant-dependent value from the same resolved Shopify variant. */
    const updateVariant = ({ updateUrl = true, clearMessage = true } = {}) => {
      const values = selectedOptions();
      selectedVariant = resolveAtharVariant(variants, variantInput.value, values);
      const valid = Boolean(selectedVariant);
      const availableForSale = Boolean(selectedVariant?.available);

      variantInput.value = valid ? selectedVariant.id : '';
      if (price) price.textContent = valid ? selectedVariant.price : '—';
      if (comparePrice) {
        comparePrice.textContent = valid ? selectedVariant.compareAtPrice : '';
        comparePrice.hidden = !selectedVariant?.onSale;
      }
      if (sku) sku.hidden = !selectedVariant?.sku;
      if (skuValue) skuValue.textContent = selectedVariant?.sku || '';
      if (material && Number.isInteger(materialOptionIndex)) material.textContent = selectedVariant?.options?.[materialOptionIndex] || '—';

      [buyNow, addToCart].forEach((button) => {
        if (button instanceof HTMLButtonElement) button.disabled = !availableForSale;
      });
      if (buyNowLabel) buyNowLabel.textContent = availableForSale ? 'Buy Now' : valid ? 'Sold out' : 'Unavailable';
      if (addToCartLabel) addToCartLabel.textContent = availableForSale ? 'Add To Cart' : valid ? 'Sold out' : 'Unavailable';
      if (availability) {
        const availabilityState = availableForSale ? 'available' : valid ? 'sold-out' : 'unavailable';
        availability.dataset.availabilityState = availabilityState;
        availability.textContent = availableForSale
          ? availability.dataset.inStockLabel || 'In stock'
          : valid
            ? availability.dataset.outOfStockLabel || 'Out of stock'
            : availability.dataset.unavailableLabel || 'Unavailable';
      }

      updateOptionAvailability(values);
      updateQuantityRule(selectedVariant);

      if (valid) {
        if (updateUrl && selectedVariant.featuredMediaId) showMedia(selectedVariant.featuredMediaId);
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', selectedVariant.id);
          history.replaceState({}, '', url);
        }
      }

      if (clearMessage && form.getAttribute('aria-busy') !== 'true') setMessage();
    };

    optionFieldsets.forEach((fieldset) => {
      fieldset.querySelectorAll('input').forEach((input) => input.addEventListener('change', () => updateVariant()));
    });

    productRoot.querySelectorAll('[data-athar-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => showMedia(button.dataset.atharMediaTarget || button.dataset.atharThumbnail, button));
    });

    const productThumbnails = () => Array.from(productRoot.querySelectorAll('[data-athar-thumbnail]'));
    productRoot.querySelector('[data-athar-gallery-prev]')?.addEventListener('click', () => {
      const thumbnails = productThumbnails();
      const activeIndex = thumbnails.findIndex((button) => button.classList.contains('is-active'));
      const nextButton = thumbnails[(activeIndex - 1 + thumbnails.length) % thumbnails.length];
      showMedia(nextButton?.dataset.atharMediaTarget || nextButton?.dataset.atharThumbnail, nextButton);
    });
    productRoot.querySelector('[data-athar-gallery-next]')?.addEventListener('click', () => {
      const thumbnails = productThumbnails();
      const activeIndex = thumbnails.findIndex((button) => button.classList.contains('is-active'));
      const nextButton = thumbnails[(activeIndex + 1) % thumbnails.length];
      showMedia(nextButton?.dataset.atharMediaTarget || nextButton?.dataset.atharThumbnail, nextButton);
    });

    productRoot.querySelectorAll('[data-athar-quantity]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!(quantity instanceof HTMLInputElement)) return;
        const step = Number(quantity.step) || 1;
        quantity.value = String(Number(quantity.value || quantity.min || 1) + (button.dataset.atharQuantity === 'increase' ? step : -step));
        normalizeQuantity();
      });
    });
    quantity?.addEventListener('change', normalizeQuantity);
    quantity?.addEventListener('blur', normalizeQuantity);

    const mechanismRail = productRoot.querySelector('[data-athar-mechanism-rail]');
    const scrollMechanism = (direction) => {
      if (!(mechanismRail instanceof HTMLElement)) return;
      const item = mechanismRail.querySelector('figure');
      const distance = item instanceof HTMLElement ? item.offsetWidth + 28 : 240;
      mechanismRail.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };
    productRoot.querySelector('[data-athar-mechanism-prev]')?.addEventListener('click', () => scrollMechanism(-1));
    productRoot.querySelector('[data-athar-mechanism-next]')?.addEventListener('click', () => scrollMechanism(1));

    const revealGroups = [
      Array.from(productRoot.querySelectorAll('.athar-product__mechanism-step')),
      Array.from(productRoot.querySelectorAll('.athar-product__care-heading, .athar-product__care-intro')),
      Array.from(productRoot.querySelectorAll('.athar-product__tip')),
    ];
    const revealItems = revealGroups.flat();
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      revealGroups.forEach((group) => {
        group.forEach((item, index) => {
          item.classList.add('athar-reveal');
          item.style.setProperty('--athar-reveal-delay', `${index * 120}ms`);
        });
      });
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      revealItems.forEach((item) => revealObserver.observe(item));
    }

    /**
     * Add to Cart keeps the customer in context and opens the shared ATHAR cart
     * drawer after Shopify has reconciled the line. Buy Now adds the identical
     * line first, then hands off to /cart so variant, quantity, and properties survive.
     */
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : addToCart;
      const isBuyNow = submitter?.hasAttribute('data-athar-buy-now');

      if (!variantInput.value || !selectedVariant) {
        setMessage('Choose a valid combination before continuing.');
        return;
      }
      if (!selectedVariant.available) {
        setMessage('This option is sold out.');
        return;
      }
      if (!form.reportValidity()) {
        setMessage('Complete the required customization fields before continuing.');
        return;
      }

      const itemCount = normalizeQuantity();
      const formData = new FormData(form);
      formData.set('id', selectedVariant.id);
      formData.set('quantity', String(itemCount));

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));

      const deferredEvent = CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: selectedVariant.id, quantity: itemCount }],
          promise: deferredEvent.promise,
        })
      );

      form.setAttribute('aria-busy', 'true');
      [buyNow, addToCart].forEach((button) => {
        if (button instanceof HTMLButtonElement) button.disabled = true;
      });
      if (submitter === buyNow && buyNowLabel) buyNowLabel.textContent = 'Adding…';
      if (submitter === addToCart && addToCartLabel) addToCartLabel.textContent = 'Adding…';
      setMessage('Adding to cart…');

      try {
        const response = await fetch(Theme.routes.cart_add_url || `${form.action}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this product to the cart.');

        const cartResponse = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('The item was added, but the cart could not be refreshed.');
        const ajaxCart = await cartResponse.json();
        deferredEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: false,
            itemCount,
            items: ajaxCart.items,
            productId: productRoot.dataset.productId,
            sections: data.sections,
            source: 'athar-product',
          },
        });

        const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
        const postAddAction = resolveAtharPostAddAction({
          isBuyNow,
          canOpenCartDrawer: Boolean(cartDrawer && typeof cartDrawer.open === 'function'),
        });

        if (postAddAction === 'redirect') {
          window.location.assign(checkoutUrl);
          return;
        }

        setMessage('Added to cart.');
        if (postAddAction === 'drawer') {
          await new Promise((resolve) => requestAnimationFrame(resolve));
          cartDrawer.open();
          setMessage();
        }
      } catch (error) {
        deferredEvent.reject(error);
        const errorMessage = error instanceof Error ? error.message : 'Unable to add this product to the cart.';
        form.dispatchEvent(new CartErrorEvent({ error: errorMessage, code: 'INVALID' }));
        setMessage(errorMessage);
      } finally {
        form.removeAttribute('aria-busy');
        updateVariant({ updateUrl: false, clearMessage: false });
      }
    });

    updateVariant({ updateUrl: false });
  });
}

document.addEventListener('DOMContentLoaded', () => initializeAtharProduct());
document.addEventListener('shopify:section:load', (event) => initializeAtharProduct(event.target));
